import type { ESQueryModel } from '@repo/api-codegen';
import type { FilterOption } from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
	FilterOptionType,
	FilterType,
} from '@repo/common/components/Filter/types';
import { EntityType } from '@repo/common/enums/entityType';
import { useCallback, useState } from 'react';
import type { IBaseModel, ISecodaEntity } from '../../api';
import {
	usersGetItemsByIdFn,
	usersGetItemsFn,
} from '../../components/Filter/filterConfigUtils';
import { useQueryDrawer } from '../../features/QueryDrawer/hooks/useQueryDrawer';
import { TableV2 } from '../TableV2';
import { useActions, useEntityQueryList, useQueryColumns } from './hooks';
import { QueryListLookbackDaysButton } from './QueryListLookbackDaysButton';
const QUERY_FILTER_OPTIONS: FilterOption[] = [
	{
		label: 'User',
		type: FilterOptionType.OWNERS,
		field: 'user_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search users',
			multipleCountsSuffix: 'users',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: usersGetItemsFn,
			getItemsById: usersGetItemsByIdFn,
			hasIsNotSetOption: true,
		},
	},
	{
		label: 'Last Run Time',
		type: FilterOptionType.RUN_DATE,
		field: 'finished_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.IsOnOrBefore,
		},
	},
];

export interface IQueryListProps {
	entity: ISecodaEntity;
}

export function QueryTable({ entity }: IQueryListProps) {
	const { openQuery } = useQueryDrawer();

	const queryColumns = useQueryColumns();

	const actions = useActions();

	const [lookBackDays, setLookBackDays] = useState(7);

	const entityQueryList = useEntityQueryList(entity?.id, lookBackDays);

	const handleCellClick = useCallback(
		(cell: { record: ESQueryModel & IBaseModel }) => {
			openQuery(cell.record);
		},
		[openQuery]
	);

	return (
		<TableV2<ESQueryModel & IBaseModel>
			pluralTypeString="queries"
			columns={queryColumns}
			columnVisibility={{
				catalogType: EntityType.query,
				catalogServerType: EntityType.query,
				catalogEntityId: entity?.id,
			}}
			withAdditionalButtons={
				<QueryListLookbackDaysButton
					lookBackDays={lookBackDays}
					handleSetLookbackDays={setLookBackDays}
					options={[1, 7, 30]}
				/>
			}
			// @ts-ignore
			usePaginationList={entityQueryList}
			paginationSize={20}
			withSearch
			withCheckbox={false}
			withFilterOptions={QUERY_FILTER_OPTIONS}
			withActions={actions}
			withStickyColumn={false}
			withInteractiveHeader
			defaultSort={null}
			onCellClick={handleCellClick}
		/>
	);
}
